import React from 'react';
import { useStaffTraining } from '../context/StaffTrainingContext';
import { trainingDate } from '../trainingUi';
import { 
  FiClock, 
  FiAlertCircle, 
  FiBookOpen, 
  FiUsers, 
  FiChevronRight, 
  FiCalendar 
} from 'react-icons/fi';

export default function TrainingActionPanels() {
  const { data, setPanel, emptyMessage } = useStaffTraining();
  const summary = data?.summary;
  const count = key => summary ? summary[key] : '—';
  const actionsData = [
    {
      id: 1,
      title: 'Overdue Training',
      count: count("overdue"),
      subtext: 'Staff requiring renewal',
      icon: FiAlertCircle,
      iconBg: 'bg-rose-50 border border-rose-100',
      iconColor: 'text-rose-600',
      countBg: 'bg-rose-50 text-rose-600 font-bold',
    },
    {
      id: 2,
      title: 'Due Soon (≤ 30 days)',
      count: count("dueSoon"),
      subtext: 'Staff requiring renewal',
      icon: FiClock,
      iconBg: 'bg-amber-50 border border-amber-100',
      iconColor: 'text-amber-500',
      countBg: 'bg-amber-50 text-amber-600 font-bold',
    },
    {
      id: 3,
      title: 'Missing Induction',
      count: count("missingInduction"),
      subtext: 'Staff without induction records',
      icon: FiBookOpen,
      iconBg: 'bg-purple-50 border border-purple-100',
      iconColor: 'text-purple-600',
      countBg: 'bg-purple-50 text-purple-500 font-medium',
    },
    {
      id: 4,
      title: 'Expiring Certificates (≤ 30 days)',
      count: count("expiringStaff"),
      subtext: 'Staff with recorded expiry dates',
      icon: FiUsers,
      iconBg: 'bg-blue-50 border border-blue-100',
      iconColor: 'text-blue-600',
      countBg: 'bg-blue-50 text-blue-500 font-medium',
    },
  ];

  const upcomingData = (data?.upcoming || []).slice(0, 4).map(item => ({
    id: `${item.staffId}-${item.categoryId}`, title: item.staffName + ' – ' + item.name,
    date: trainingDate(item.expiresOn), badgeText: item.days === 0 ? 'Due today' : 'Due in ' + item.days + ' days',
  }));

  return (
    <div className="w-full max-w-[480px] flex flex-col gap-5 font-sans bg-slate-50/50 p-1">
      
      {/* 1. Training Action Required Card */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        {/* Header Block */}
        <div className="px-4 py-3.5 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-900 tracking-tight">
              Training Action Required
            </h3>
            <span className="px-1.5 py-0.5 text-xs font-bold bg-rose-50 text-rose-500 rounded border border-rose-100/60">
              {data?.actions.length ?? '—'}
            </span>
          </div>
          <button onClick={() => setPanel("actions")} className="text-xs font-semibold text-blue-600 hover:underline">
            View all
          </button>
        </div>

        {/* Action Required Items List */}
        <div className="divide-y divide-slate-100">
          {actionsData.map((item) => {
            const Icon = item.icon;
            return (
              <button 
                key={item.id}
                onClick={() => setPanel(item.id === 1 ? 'overdue' : item.id === 2 ? 'dueSoon' : item.id === 3 ? 'induction' : 'calendar')}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50/60 transition text-left group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Left Action Module Icon */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.iconBg}`}>
                    <Icon className={`w-4 h-4 ${item.iconColor}`} />
                  </div>
                  
                  {/* Count Indicator Badge */}
                  <div className={`w-6 h-5 rounded text-[11px] flex items-center justify-center shrink-0 ${item.countBg}`}>
                    {item.count}
                  </div>

                  {/* Title text configuration info */}
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-slate-800 tracking-tight truncate">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                      {item.subtext}
                    </span>
                  </div>
                </div>

                <FiChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition ml-2" />
              </button>
            );
          })}
        </div>
      </div>


      {/* 2. Upcoming Training / Renewals Card */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        {/* Header Block */}
        <div className="px-4 py-3.5 flex items-center justify-between border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-900 tracking-tight">
            Upcoming Training / Renewals
          </h3>
          <button onClick={() => setPanel("calendar")} className="text-xs font-semibold text-blue-600 hover:underline">
            View calendar
          </button>
        </div>

        {/* Dynamic Renewal items block row context map layout */}
        <div className="divide-y divide-slate-100">
          {upcomingData.map((item) => (
            <div 
              key={item.id}
              className="px-4 py-3 flex items-center justify-between gap-4"
            >
              <div className="flex items-start gap-3 min-w-0">
                {/* Standard Calendar Node Icon block wrapper */}
                <div className="w-8 h-8 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center text-slate-700 shrink-0">
                  <FiCalendar className="w-4 h-4" />
                </div>

                {/* Training descriptions label metadata stack layout block */}
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-bold text-slate-800 tracking-tight truncate">
                    {item.title}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                    {item.date}
                  </span>
                </div>
              </div>

              {/* Status Warning Deadline Text highlight tag context container */}
              <span className="px-2 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded border border-amber-100/60 shrink-0 tracking-wide">
                {item.badgeText}
              </span>
            </div>
          ))}{!upcomingData.length && <p className="p-4 text-xs text-slate-400">{data ? 'No renewals due within 30 days. Expiry dates are optional.' : emptyMessage}</p>}
        </div>

        {/* Footer Deep-Link Action Container */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/30">
          <button onClick={() => setPanel("calendar")} className="text-xs font-bold text-blue-600 hover:underline">
            View full calendar
          </button>
        </div>
      </div>

    </div>
  );
}
